// Import necessary libraries
import * as XLSX from "xlsx";
import jsPDF from "jspdf"; // Import jspdf for PDF export
import moment from "moment";
import { format } from "date-fns";

export const handleExportExcel = (users) => {
  // Check if users array is defined and not empty
  if (!users || !Array?.isArray(users) || users?.length === 0) {
    console.log("No user data available for export.");
    return;
  }

  // Prepare data for Excel export
  const excelData = users?.map((user) => ({
    "User Id": user?.user_id,
    Name: `${user?.user_first_name} ${user?.user_last_name}`,
    State: user?.user_address,
    City: user?.user_city,
  }));

  // Convert data to Excel format
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Create Excel workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

  // Generate buffer from workbook
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "buffer",
  });

  // Save as a file
  const fileName = "users_data.xlsx";
  saveAsExcelFile(excelBuffer, fileName);
};

// Function to trigger download
const saveAsExcelFile = (buffer, fileName) => {
  const data = new Blob([buffer], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

// Function to handle exporting data to PDF
export const handleExportPDF = (users) => {
  const doc = new jsPDF();
  doc.text("Users List", 10, 10);

  let yPos = 20;
  users?.forEach((user) => {
    doc.text(`User Id: ${user?.user_id}`, 10, yPos);
    doc.text(
      `Name: ${user?.user_first_name} ${user?.user_last_name}`,
      50,
      yPos
    );
    doc.text(`State: ${user?.user_address}`, 100, yPos);
    doc.text(`City: ${user?.user_city}`, 150, yPos);
    yPos += 10; // Adjust vertical position for the next entry
  });

  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);

  // Create a temporary anchor element and trigger the download using a helper function
  downloadFile(url, "users.pdf");
};

// Utility function to download a file
const downloadFile = (url, filename) => {
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();

  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const formatDate = (dateString) => {
  if (!dateString) {
    return <p>Nil</p>;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return null;
  }

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`; // Format as MM/DD/YYYY
};

// date and time decode seperate
export function formatTimestamp(timestamp) {
  const date = moment(timestamp);
  return {
    formattedDate: date.format("YYYY-MM-DD"),
    formattedTime: date.format("HH:mm:ss"),
  };
}

export function formatTimestamps(timestamps) {
  return timestamps.map((timestamp) => formatTimestamp(timestamp));
}

export const formattedDate = (timestamp) => {
  if (!timestamp) return "";
  return format(new Date(timestamp), "dd-MM-yyyy"); // Format as "YYYY-MM-DD"
};

// Function to convert UTC to IST
export const convertUTCToIST = (utcDate) => {
  const date = new Date(utcDate);
  date.setHours(date.getHours() + 5);
  date.setMinutes(date.getMinutes() + 30);
  return date.toLocaleTimeString("en-IN", { hour12: true }); // Adjust formatting as needed
};

export const formatDateToAPI = (date) => {
  const year = date?.getFullYear();
  const month = String(date?.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so add 1
  const day = String(date?.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

